import { getEmptyTemplate } from '@/utils/emptyTemplate';
import { GRADUATION_NAME } from '@/littleWedding/projectConstantes';

import { defineStore } from 'pinia';
import { computed, ref, toRaw } from 'vue';

import type { ITemplate } from '@/typescript/interfaces/ITemplate';
import type { IField } from '@/typescript/interfaces/IField';
import type { ITextField } from '@/typescript/interfaces/ITextField';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { ITextboxField } from '@/typescript/interfaces/ITextboxField';

const useTemplateStore = defineStore('template', () => {
    const selectedTemplate = ref<ITemplate>(getEmptyTemplate());
    const selectedFieldId = ref<string>();
    const selectedTextIndexes = ref<{ end: number; start: number }>();

    const selectedTemplateConfiguration = computed(() => selectedTemplate.value.configuration);
    const selectedField = computed(() =>
        Object.values(selectedTemplate.value.configuration.fields).find((field) => field.id === selectedFieldId.value),
    );

    function updateField(field: IField) {
        const labelIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        if (labelIndex >= 0) {
            // A stored selection index only makes sense for the text it was captured on. Style edits
            // keep the same text and must not lose the selection, so only a text change invalidates it.
            const previousField = selectedTemplateConfiguration.value.fields[labelIndex];
            if ((previousField as ITextField)?.text !== (field as ITextField)?.text) {
                selectedTextIndexes.value = undefined;
            }
            selectedTemplateConfiguration.value.fields[labelIndex] = field;
        }
        if ((field as IImageField)?.uploadType === 'cutPath') {
            const duplicateFieldIndex = selectedTemplateConfiguration.value.fields.find(
                (currentField) => currentField.id === `${field.id}-verso`,
            );
            if (duplicateFieldIndex) {
                duplicateFieldIndex.transform = { ...field.transform };
            }
        }
    }

    function addFields(field: IField[]) {
        field.forEach((f) => {
            addField(f);
        });
    }

    function addField(field: IField) {
        selectedTemplateConfiguration.value.fields.push(field);
        selectedFieldId.value = field.id;
    }

    function removeField(field: IField) {
        const labelIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        if (labelIndex >= 0) {
            selectedTemplateConfiguration.value.fields.splice(labelIndex, 1);
        }
    }

    function getFieldById(id: string): IImageField | ITextboxField | ITextField | undefined {
        return selectedTemplateConfiguration.value.fields.find((field) => field.id === id) as
            | IImageField
            | ITextboxField
            | ITextField
            | undefined;
    }

    function getFilledFields(): IField[] {
        const fields = toRaw(selectedTemplateConfiguration.value.fields);
        return fields.filter((field) => {
            if ((field as ITextField).defaultText) {
                return (field as ITextField).text !== undefined;
            }
            return (field as IImageField).url !== undefined;
        });
    }

    function bringFieldForward(field: IField) {
        const currentIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        if (currentIndex < selectedTemplateConfiguration.value.fields.length - 1) {
            const nextIndex = currentIndex + 1;
            selectedTemplateConfiguration.value.fields.splice(currentIndex, 1, selectedTemplateConfiguration.value.fields[nextIndex]);
            selectedTemplateConfiguration.value.fields.splice(nextIndex, 1, field);
            setGraduationToFront();
        }
    }

    function bringFieldBackward(field: IField) {
        const currentIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        if (currentIndex > 0) {
            const previousIndex = currentIndex - 1;
            selectedTemplateConfiguration.value.fields.splice(currentIndex, 1, selectedTemplateConfiguration.value.fields[previousIndex]);
            selectedTemplateConfiguration.value.fields.splice(previousIndex, 1, field);
            setGraduationToFront();
        }
    }

    function bringFieldToFront(field: IField) {
        const currentIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        selectedTemplateConfiguration.value.fields.splice(currentIndex, 1);
        selectedTemplateConfiguration.value.fields.push(field);
        setGraduationToFront();
    }

    function bringFieldToBack(field: IField) {
        const currentIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        selectedTemplateConfiguration.value.fields.splice(currentIndex, 1);
        selectedTemplateConfiguration.value.fields.unshift(field);
        setGraduationToFront();
    }

    function setGraduationToFront() {
        const graduationField = selectedTemplateConfiguration.value.fields.find((field) => {
            if (!('filename' in field)) {
                return false;
            }
            return (field as IImageField).filename === GRADUATION_NAME;
        });
        if (graduationField) {
            const currentIndex = selectedTemplateConfiguration.value.fields.findIndex(
                (currentField) => currentField.id === graduationField.id,
            );
            selectedTemplateConfiguration.value.fields.splice(currentIndex, 1);
            selectedTemplateConfiguration.value.fields.push(graduationField);
        }
    }

    return {
        addField,
        addFields,
        bringFieldBackward,
        bringFieldForward,
        bringFieldToBack,
        bringFieldToFront,
        getFieldById,
        getFilledFields,
        removeField,
        selectedTemplate,
        selectedTemplateConfiguration,
        selectedField,
        selectedFieldId,
        setGraduationToFront,
        updateField,
        selectedTextIndexes,
    };
});

export { useTemplateStore };
